import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, CreditCard, X } from 'lucide-react';
import { subscriptionService } from '../services/subscriptionService';
import { useBusinessContext } from '../contexts/BusinessContext';

const TrialBanner = () => {
  const { isOwner } = useBusinessContext(); // ✅ Check if user is owner
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // ✅ ONLY load subscription info for owners
    if (isOwner) {
      loadSubscriptionInfo();
    } else {
      setLoading(false); // Employees don't need this
    }
  }, [isOwner]);

  const loadSubscriptionInfo = async () => {
    try {
      setLoading(true);
      
      // Get current subscription status from API (not JWT)
      const subscription = await subscriptionService.getStatus();
      setSubscriptionInfo(subscription);

      // Show banner ONLY if status is Trial or Incomplete AND trial is still active
      const shouldShowBanner = 
        (subscription.subscriptionStatus === 'Trial' || 
         subscription.subscriptionStatus === 'Incomplete') &&
        subscription.isTrialActive &&
        !subscription.isTrialExpired;

      setShowBanner(shouldShowBanner);
    } catch (error) {
      console.error('Error loading subscription info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('trialBannerDismissed', 'true');
  };

  // ✅ Don't show banner for employees
  if (!isOwner) {
    return null;
  }

  // Don't show banner if loading, dismissed, not in trial, or has active subscription
  if (loading || dismissed || !showBanner || !subscriptionInfo) {
    return null;
  }

  // Extra safety check - don't show if Active subscription
  if (subscriptionInfo.subscriptionStatus === 'Active') {
    return null;
  }

  const daysRemaining = subscriptionInfo.trialDaysRemaining || 0;
  const isExpiringSoon = daysRemaining <= 3;

  // Determine banner color based on days remaining
  const getBannerColor = () => {
    if (daysRemaining <= 1) return 'bg-red-50 border-red-500';
    if (daysRemaining <= 3) return 'bg-orange-50 border-orange-500';
    return 'bg-blue-50 border-blue-500';
  };

  const getTextColor = () => {
    if (daysRemaining <= 1) return 'text-red-900';
    if (daysRemaining <= 3) return 'text-orange-900';
    return 'text-blue-900';
  };

  const getIconColor = () => {
    if (daysRemaining <= 1) return 'text-red-600';
    if (daysRemaining <= 3) return 'text-orange-600';
    return 'text-blue-600';
  };

  return (
    <div className={`${getBannerColor()} border-l-4 rounded-lg p-4 mt-4 mb-4 relative`}>
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
        aria-label="Dismiss banner"
      >
        <X size={18} />
      </button>

      <div className="flex items-start">
        <div className="flex-shrink-0">
          <Clock className={`h-5 w-5 ${getIconColor()}`} />
        </div>
        <div className="ml-3 flex-1">
          <h3 className={`text-sm font-semibold ${getTextColor()} mb-1`}>
            {isExpiringSoon ? '⚠️ Trial Ending Soon' : 'Free Trial Active'}
          </h3>
          <p className={`text-sm ${getTextColor()} mb-3`}>
            {daysRemaining === 0 
              ? 'Your trial ends today! Subscribe now to keep access to all your data.'
              : daysRemaining === 1
              ? 'Your trial ends tomorrow. Subscribe now to continue using SuddyFlow.'
              : `You have ${daysRemaining} days left in your free trial.`
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Link
              to="/subscribe"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <CreditCard className="mr-2 h-4 w-4" />
              Subscribe Now
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              View Plans
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrialBanner;